'use client'

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useShow } from '@/contexts/ShowContext'
import { apiFetch } from '@/lib/api'

/**
 * Every asset the editor currently references, keyed by id.
 *
 * Mention nodes only carry `{ assetId, name, mediaType }`, so the chips have no
 * url of their own to render a thumbnail from and nothing to hand a preview
 * modal. The host's `resolveAsset` only covers assets on the grid's current
 * page, which a mention outlives — so the records live here instead, seeded on
 * drop and filled in from the api for ids this store hasn't seen.
 *
 * The default value is an empty store so editors that don't provide it (the
 * script editor) degrade to icons rather than crashing.
 */
const EditorAssetsContext = createContext({
    assetsById: {},
    getAsset: () => null,
    registerAssets: () => {},
    ensureAssets: () => {},
})

export const useEditorAssets = () => useContext(EditorAssetsContext)

const byId = (assets) =>
    assets.reduce((accumulator, asset) => {
        if (asset?._id) accumulator[asset._id] = asset
        return accumulator
    }, {})

export function EditorAssetsProvider({ children }) {
    const { show } = useShow()
    const [assetsById, setAssetsById] = useState({})
    // Ids already requested, so a chip re-rendering mid-flight doesn't refetch.
    const requestedIdsRef = useRef(new Set())

    const registerAssets = useCallback((assets) => {
        const incoming = byId(Array.isArray(assets) ? assets : [assets])
        if (Object.keys(incoming).length === 0) return
        Object.keys(incoming).forEach((id) => requestedIdsRef.current.add(id))
        setAssetsById((previous) => ({ ...previous, ...incoming }))
    }, [])

    // Back-fills records the editor never saw dropped: chips restored from a saved
    // artifact/prompt, or ones whose asset has scrolled off the grid's current page,
    // arrive with only an id. Fetches just the ids this store hasn't already got.
    const ensureAssets = useCallback(async (assetIds) => {
        if (!show?._id) return
        const unknownIds = (assetIds || []).filter((id) => {
            if (typeof id !== 'string') return false
            return !requestedIdsRef.current.has(id)
        })
        if (unknownIds.length === 0) return

        unknownIds.forEach((id) => requestedIdsRef.current.add(id))

        const fetched = await Promise.all(
            unknownIds.map(async (id) => {
                try {
                    const response = await apiFetch(`/api/shows/${show._id}/assets/${id}`)
                    if (!response.ok) return null
                    return await response.json()
                } catch (error) {
                    console.error('Failed to load a mentioned asset:', error)
                    return null
                }
            }),
        )

        const resolved = fetched.filter(Boolean)
        if (resolved.length === 0) return
        setAssetsById((previous) => ({ ...previous, ...byId(resolved) }))
    }, [show?._id])

    const value = useMemo(
        () => ({
            assetsById,
            getAsset: (id) => assetsById[id] || null,
            registerAssets,
            ensureAssets,
        }),
        [assetsById, registerAssets, ensureAssets],
    )

    return (
        <EditorAssetsContext.Provider value={value}>
            {children}
        </EditorAssetsContext.Provider>
    )
}
